import React, { useState, useEffect, useRef } from 'react'
import { Dialog } from '@headlessui/react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../contexts/ThemeContext'
import { db, storage, updateDocument, getDocument } from '../services/firebase'
import { collection, query, orderBy, getDocs, doc, onSnapshot } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { motion } from 'framer-motion'
import { format } from 'date-fns'
import { getCurrentPosition, getCityFromCoords, getDerivedMatchStatus, calculatePoints } from '../utils/helpers'
import { requestNotificationPermission, saveDeviceToken } from '../services/notifications'
import {
  PencilIcon,
  CalendarIcon,
  MapPinIcon,
  CameraIcon,
  XMarkIcon,
  CheckCircleIcon,
  TrashIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import logo from '../assets/logo.png'

const Profile = () => {
  const { userId } = useParams()
  const { user, userData: currentUserData, updateUserData, logout, setUserData } = useAuth()
  const { t } = useTranslation()
  const { darkMode, setDarkModePreference } = useTheme()
  const navigate = useNavigate()

  const [profileUser, setProfileUser] = useState(null)
  const [userMatches, setUserMatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({})
  const [isFollowing, setIsFollowing] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [historyTab, setHistoryTab] = useState('organized')
  const [detectingLocation, setDetectingLocation] = useState(false)
  const [notificationPermission, setNotificationPermission] = useState(() => 
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'default'
  )
  const [pointRules, setPointRules] = useState({
    win: 0.02,
    draw: 0.01,
    loss: 0.02,
    violation: 0.01
  })

  const profileImageRef = useRef(null)

  const profileId = userId || user?.uid
  const isOwnProfile = profileId === user?.uid
  const cardClass = darkMode ? 'bg-sport-darker border-2 border-sport-silver/35 text-sport-silver hover:border-sport-silver/55 py-3 px-6' : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-gray-300 py-3 px-6'
  const lightTextClass = darkMode ? 'text-sport-light' : 'text-gray-900'
  const mutedTextClass = darkMode ? 'text-sport-silver/85' : 'text-gray-600'
  const mutedTextLightClass = darkMode ? 'text-sport-silver/70' : 'text-gray-500'
  const mutedTextLighterClass = darkMode ? 'text-sport-silver/55' : 'text-gray-400'
  const mutedTextLightestClass = darkMode ? 'text-sport-silver/45' : 'text-gray-400'
  const statsLabelClass = darkMode ? 'text-sport-silver/65' : 'text-gray-500'
  const modalBackdropClass = darkMode ? 'bg-sport-darker/80' : 'bg-gray-900/50'
  const modalPanelClass = darkMode ? 'bg-sport-dark/98 border border-sport-primary/35' : 'bg-white border border-gray-200'
  const badgeClass = darkMode ? 'bg-sport-darker/80 border border-sport-primary/35 text-sport-light' : 'bg-gray-100 border border-sport-primary/35 text-gray-800'
  const avatarBgClass = darkMode ? 'bg-sport-darker/80' : 'bg-gray-200'
  const filterTabBgClass = darkMode ? 'bg-sport-darker/40' : 'bg-gray-100'
  const tabActiveClass = darkMode ? 'bg-sport-primary/20 text-sport-primary' : 'bg-sport-primary text-white'
  const tabInactiveClass = darkMode ? 'text-sport-silver/70 hover:text-sport-light hover:bg-white/5' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'

  useEffect(() => {
    if (!profileId) return

    const unsubscribe = onSnapshot(doc(db, 'users', profileId), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const userData = { id: docSnapshot.id, ...docSnapshot.data() }
        setProfileUser(userData)
        setEditForm(userData)

        if (currentUserData?.following) {
          setIsFollowing(currentUserData.following.includes(profileId))
        }
      } else {
        toast.error(t('profile.userNotFound', { defaultValue: 'User not found' }))
        navigate('/')
      }
      setLoading(false)
    })

    fetchUserMatches()
    fetchPointRules()
    return () => unsubscribe()
  }, [profileId, currentUserData?.following])

  const fetchPointRules = async () => {
    try {
      const settingsDoc = await getDocument('settings', 'pointsRules')
      if (!settingsDoc.exists()) return

      const data = settingsDoc.data() || {}
      setPointRules({
        win: Number(data.win ?? 0.02),
        draw: Number(data.draw ?? 0.01),
        loss: Math.abs(Number(data.loss ?? 0.02)),
        violation: Math.abs(Number(data.violation ?? 0.01))
      })
    } catch (error) {
      console.error('Error fetching point rules:', error)
    }
  }

  const fetchUserMatches = async () => {
    try {
      const matchesQuery = query(collection(db, 'matches'), orderBy('date', 'desc'))
      const matchesSnapshot = await getDocs(matchesQuery)
      const matchesData = matchesSnapshot.docs
        .map((matchDoc) => ({ id: matchDoc.id, ...matchDoc.data() }))
        .filter((match) => {
          const isOrganizer = match.organizerId === profileId
          const isParticipant = match.participants?.some((participant) => participant.userId === profileId)
          return isOrganizer || isParticipant
        })

      setUserMatches(matchesData)
    } catch (error) {
      console.error('Error fetching matches:', error)
    }
  }

  const handleFollow = async () => {
    if (!user || !profileId || !currentUserData) return

    try {
      const nextIsFollowing = !isFollowing
      const updatedFollowing = isFollowing
        ? currentUserData.following.filter((id) => id !== profileId)
        : Array.from(new Set([...(currentUserData.following || []), profileId]))

      await updateDocument('users', user.uid, { following: updatedFollowing })

      setIsFollowing(nextIsFollowing)
      setUserData((prev) => (prev ? { ...prev, following: updatedFollowing } : prev))

      const updatedFollowers = isFollowing
        ? (profileUser.followers || []).filter((id) => id !== user.uid)
        : Array.from(new Set([...(profileUser.followers || []), user.uid]))

      try {
        await updateDocument('users', profileId, { followers: updatedFollowers })
      } catch (followersError) {
        const code = String(followersError?.code || '')
        const isPermissionIssue = code.includes('permission-denied') || code.includes('insufficient-permission')
        if (!isPermissionIssue) {
          console.warn('Non-blocking followers update issue:', followersError)
        }
      }

      toast.success(isFollowing ? t('profile.unfollowed', { defaultValue: 'No longer following this user' }) : t('profile.followed', { defaultValue: 'Now following this user' }))
    } catch (error) {
      console.error('Error following user:', error)
      toast.error(t('profile.followError', { defaultValue: 'Error following user' }))
    }
  }

  const handleUpdateProfile = async () => {
    try {
      const normalizedPlayerNumber = String(editForm.playerNumber ?? '')
        .replace(/\D/g, '')
        .slice(0, 2)

      const updatedProfile = {
        ...editForm,
        playerNumber: normalizedPlayerNumber
      }

      await updateUserData(updatedProfile)
      setProfileUser((prev) => ({ ...prev, ...updatedProfile }))
      setIsEditing(false)
      toast.success(t('profile.updatedSuccess', { defaultValue: 'Profile updated successfully!' }))
    } catch (error) {
      console.error('Error updating profile:', error)
      toast.error(t('profile.updateError', { defaultValue: 'Error updating profile' }))
    }
  }

  const handleImageUpload = async (file) => {
    if (!file) return

    setUploadingImage(true)
    try {
      const timestamp = Date.now()
      const fileExt = file.name.split('.').pop()
      const fileName = `profile_${timestamp}.${fileExt}`
      const storagePath = `users/${profileId}/${fileName}`
      const storageRef = ref(storage, storagePath)

      const snapshot = await uploadBytes(storageRef, file)
      const downloadUrl = await getDownloadURL(snapshot.ref)
      const oldImageUrl = profileUser?.profileImage

      const updateData = { profileImage: downloadUrl, updatedAt: new Date().toISOString() }
      await updateDocument('users', profileId, updateData)
      setProfileUser((prev) => ({ ...prev, ...updateData }))

      if (isOwnProfile) {
        await updateUserData(updateData)
      }

      if (oldImageUrl) {
        try {
          const oldPath = decodeURIComponent(oldImageUrl.split('/o/')[1].split('?')[0])
          const oldImageRef = ref(storage, oldPath)
          await deleteObject(oldImageRef)
        } catch (error) {
          console.log('Old image not found or already deleted:', error)
        }
      }

      toast.success(t('profile.imageUpdated', { defaultValue: 'Profile image updated!' }))
    } catch (error) {
      console.error('Error uploading image:', error)
      toast.error(t('profile.uploadError', { defaultValue: 'Error uploading image. Please try again.' }))
    } finally {
      setUploadingImage(false)
    }
  }

  const handleDeleteImage = async () => {
    const imageUrl = profileUser?.profileImage
    if (!imageUrl) return

    if (!window.confirm(t('profile.removePhotoConfirm', { defaultValue: 'Remove profile image?' }))) return

    try {
      const imagePath = decodeURIComponent(imageUrl.split('/o/')[1].split('?')[0])
      const imageRef = ref(storage, imagePath)
      await deleteObject(imageRef)

      const updateData = { profileImage: '', updatedAt: new Date().toISOString() }
      await updateDocument('users', profileId, updateData)
      setProfileUser((prev) => ({ ...prev, ...updateData }))

      if (isOwnProfile) {
        await updateUserData(updateData)
      }

      toast.success(t('profile.imageRemoved', { defaultValue: 'Profile image removed' }))
    } catch (error) {
      console.error('Error deleting image:', error)
      toast.error(t('profile.deleteImageError', { defaultValue: 'Error deleting image' }))
    }
  }

  const handleProfileImageClick = () => {
    profileImageRef.current?.click()
  }

  const handleOpenEdit = () => {
    if (!profileUser) return
    setEditForm({ ...profileUser })
    setIsEditing(true)
  }

  const handleCloseEdit = () => {
    if (profileUser) setEditForm({ ...profileUser })
    setIsEditing(false)
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error(t('profile.selectImageFile', { defaultValue: 'Please select an image file (jpg, png, gif)' }))
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('profile.imageTooLarge', { defaultValue: 'Image must be less than 5MB' }))
        return
      }

      handleImageUpload(file)
    }

    e.target.value = ''
  }

  const handleDetectLocation = async () => {
    if (!isOwnProfile) return
    
    setDetectingLocation(true)
    try {
      const position = await getCurrentPosition()
      const city = await getCityFromCoords(position.latitude, position.longitude)
      setEditForm(prev => ({
        ...prev,
        latitude: position.latitude,
        longitude: position.longitude,
        city: city || prev.city
      }))
      toast.success(t('location.locationUpdated'))
    } catch (error) {
      console.error('Error detecting location:', error)
      toast.error(t('location.errorGettingLocation'))
    } finally {
      setDetectingLocation(false)
    }
  }

  const handleLogoutFromProfile = async () => {
    await logout()
    setDarkModePreference(true)
    navigate('/login')
  }

  const handleEnableNotifications = async () => {
    const result = await requestNotificationPermission()
    if (result.success) {
      setNotificationPermission('granted')
      if (result.token && user?.uid) {
        await saveDeviceToken(user.uid, result.token)
      }
      toast.success(t('notifications.enabled', { defaultValue: 'Notifications enabled!' }))
    } else {
      const errorMsg = result.error || ''
      // iOS: Show special PWA install guide
      if (errorMsg === 'iOS_PWA') {
        toast.error(t('notifications.iOSPWA'), { duration: 8000 })
      } else if (errorMsg.includes('denied')) {
        toast.error(t('notifications.denied'), { duration: 5000 })
      } else {
        toast.error(result.error || t('notifications.enableFailed'))
      }
    }
  }

  const notificationsEnabled = notificationPermission === 'granted'

  if (loading) {
    return (
      <div className="profile-page profile-loading">
        <div className="profile-loading-spinner" aria-hidden />
        <p className={`${mutedTextLightClass} text-xs font-['Orbitron'] tracking-[0.25em] uppercase`}>{t('profile.loadingPlayer')}</p>
      </div>
    )
  }

  if (!profileUser) return null

  const sportsList = Array.isArray(profileUser?.sports)
    ? profileUser.sports
    : typeof profileUser?.sports === 'string' && profileUser.sports.trim().length > 0
      ? [profileUser.sports]
      : []

  const editSportsValue = Array.isArray(editForm?.sports)
    ? editForm.sports[0] || 'calcio'
    : editForm?.sports || 'calcio'

  const positionAbbr = (() => {
    const r = (profileUser.role || '').toLowerCase()
    if (r === 'goalkeeper') return 'GK'
    if (r === 'defender') return 'DEF'
    if (r === 'midfielder') return 'MID'
    if (r === 'attacker') return 'ATT'
    if (r === 'left') return 'L'
    if (r === 'right') return 'R'
    const sp = (sportsList[0] || editSportsValue || '').toString().toLowerCase()
    if (sp === 'padel') return 'PDL'
    if (sp === 'calcio') return 'FBL'
    return 'ATH'
  })()

  const playerNumber = (() => {
    const raw = profileUser?.playerNumber
    if (raw === null || raw === undefined || raw === '') return '--'
    const normalized = String(raw).replace(/\D/g, '').slice(0, 2)
    return normalized || '--'
  })()

  const completedContributedMatchEntries = userMatches
    .map((match) => {
      const derivedStatus = getDerivedMatchStatus(match?.date, match?.status)
      if (derivedStatus !== 'completed') return null

      if (match.organizerId === profileId) {
        return {
          match,
          participant: null
        }
      }

      const participant = match.participants?.find((entry) => entry.userId === profileId)
      if (!participant) return null

      // Count accepted players (and legacy entries without explicit status) as contributors.
      if (participant.status !== 'accepted' && participant.status !== undefined) return null

      return {
        match,
        participant
      }
    })
    .filter(Boolean)

  const completedContributedMatches = completedContributedMatchEntries.length

  const computedResultStats = completedContributedMatchEntries.reduce(
    (acc, entry) => {
      const participant = entry.participant
      if (!participant) return acc

      const result = participant.result
      if (result === 'win') acc.wins += 1
      if (result === 'loss' || result === 'lose') acc.losses += 1
      if (result === 'draw') acc.draws += 1

      const pointDelta =
        participant.pointsDelta !== undefined
          ? Number(participant.pointsDelta || 0)
          : calculatePoints(result, Boolean(participant.violated), pointRules)
      acc.points += pointDelta
      return acc
    },
    { wins: 0, losses: 0, draws: 0, points: 0 }
  )

  const hasComputedResults = completedContributedMatchEntries.some(({ participant }) =>
    participant?.result === 'win' || participant?.result === 'lose' || participant?.result === 'loss' || participant?.result === 'draw'
  )

  const effectiveWins = hasComputedResults ? computedResultStats.wins : Number(profileUser?.wins) || 0
  const effectivePoints = hasComputedResults
    ? Number(computedResultStats.points.toFixed(4))
    : Number(profileUser?.points) || 0

  const completedContributedMatchesLegacy = userMatches.filter((match) => {
    const derivedStatus = getDerivedMatchStatus(match?.date, match?.status)
    if (derivedStatus !== 'completed') return false

    if (match.organizerId === profileId) return true

    const participant = match.participants?.find((entry) => entry.userId === profileId)
    if (!participant) return false

    // Count accepted players (and legacy entries without explicit status) as contributors.
    return participant.status === 'accepted' || participant.status === undefined
  }).length

  // Composite rating scaled to 0.0-5.0 from core player performance signals.
  const playerRatingOutOfFive = (() => {
    const matches = completedContributedMatches || completedContributedMatchesLegacy
    const wins = effectiveWins
    const goals = Number(profileUser?.goals) || 0
    const points = effectivePoints
    const followers = Number(profileUser?.followers?.length) || 0

    const winRate = matches > 0 ? Math.min(1, wins / matches) : 0
    const goalsPerMatchScore = matches > 0 ? Math.min(1, goals / (matches * 2)) : 0
    const pointsScore = Math.min(1, points / 12000)
    const volumeScore = Math.min(1, matches / 40)
    const followersScore = Math.min(1, followers / 200)

    const weightedScore =
      winRate * 0.35 +
      goalsPerMatchScore * 0.2 +
      pointsScore * 0.25 +
      volumeScore * 0.15 +
      followersScore * 0.05

    return Number((weightedScore * 5).toFixed(1))
  })()

  const fmtCardStat = (n) => {
    const x = Number(n) || 0
    if (x >= 10000) return `${Math.round(x / 1000)}k`
    if (x >= 1000) return `${(x / 1000).toFixed(1).replace(/\.0$/, '')}k`
    return String(x)
  }

  const followersCount = profileUser.followers?.length || 0

  const getMatchContributionLabel = (match) => {
    const participant = match.participants?.find((entry) => entry.userId === profileId)

    if (match.organizerId === profileId) return t('common.organizer')
    if (!participant) return t('common.participant')

    switch ((participant.role || '').toLowerCase()) {
      case 'goalkeeper':
        return t('role.goalkeeper')
      case 'defender':
        return t('role.defender')
      case 'midfielder':
        return t('role.midfielder')
      case 'attacker':
        return t('role.attacker')
      case 'left':
        return t('role.left')
      case 'right':
        return t('role.right')
      default:
        return participant.status === 'accepted' ? t('common.player') : t('common.pending')
    }
  }

  const sortedUserMatches = [...userMatches].sort((a, b) => {
    const aIsOrganizer = a.organizerId === profileId ? 1 : 0
    const bIsOrganizer = b.organizerId === profileId ? 1 : 0

    if (aIsOrganizer !== bIsOrganizer) return bIsOrganizer - aIsOrganizer

    return new Date(b.date) - new Date(a.date)
  })

  const organizedMatches = sortedUserMatches.filter((match) => match.organizerId === profileId)
  const playedMatches = sortedUserMatches.filter((match) => match.organizerId !== profileId)

  return (
    <div className="profile-page max-w-6xl mx-auto space-y-8 relative px-3 sm:px-4 pb-12">
      <div className="pointer-events-none absolute -top-10 -left-10 w-80 h-80 bg-sport-primary/15 blur-3xl rounded-full" />
      <div className="pointer-events-none absolute top-1/4 -right-16 w-96 h-96 bg-sport-secondary/12 blur-3xl rounded-full" />
      <div className="pointer-events-none absolute bottom-1/3 left-1/2 -translate-x-1/2 w-[min(90vw,560px)] h-40 bg-sport-accent/5 blur-3xl rounded-full" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 280, damping: 26 }}
        className="flex flex-col items-center gap-4"
      >
        <div className="fut-card-shell">
          <div className="fut-card">
            <div className="fut-card-inner">
              <div className="fut-shard fut-shard--t" aria-hidden />
              <div className="fut-shard fut-shard--l" aria-hidden />
              <div className="fut-shard fut-shard--r" aria-hidden />

              <div className="fut-card-top">
                <div className="fut-ovr-block">
                  <span className="fut-ovr-num tabular-nums">{playerNumber}</span>
                  <span className="fut-ovr-pos">{positionAbbr}</span>
                </div>
                <div className="fut-emblem">
                  <img src={logo} alt={t('app.brand')} />
                </div>
                <div aria-hidden className="min-w-0 w-10 sm:w-12 shrink-0" />
              </div>

              <div className="fut-portrait-wrap relative">
                {profileUser?.profileImage ? (
                  <img
                    src={profileUser.profileImage}
                    alt={`${profileUser?.firstName || ''} ${profileUser?.lastName || ''}`.trim() || 'Profile'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                ) : (
                  <div className="fut-portrait-initials">
                    {profileUser?.firstName?.[0]}
                    {profileUser?.lastName?.[0]}
                  </div>
                )}
              </div>

              <div className="fut-name-plate">
                <h1 className="fut-player-name">
                  {[profileUser?.firstName, profileUser?.lastName].filter(Boolean).join(' ').toUpperCase() ||
                    'PLAYER'}
                </h1>
                <div className="fut-player-city">
                  <MapPinIcon className="h-3 w-3 text-sport-primary shrink-0" aria-hidden />
                  {profileUser?.city || t('common.locationTbd')}
                </div>
              </div>

              <div className="fut-stats-block">
                {[
                  ['PTS', fmtCardStat(effectivePoints)],
                  ['WIN', fmtCardStat(effectiveWins)],
                  ['MCH', fmtCardStat(completedContributedMatches || completedContributedMatchesLegacy)],
                  ['GLS', fmtCardStat(profileUser?.goals)],
                  ['RTG', `${playerRatingOutOfFive.toFixed(1)}/5`],
                  ['FOL', fmtCardStat(followersCount)]
                ].map(([abbr, val]) => (
                  <div className="fut-stat-line" key={abbr}>
                    <span className="fut-stat-label">{abbr}</span>
                    <span className="fut-stat-val tabular-nums">{val}</span>
                  </div>
                ))}
              </div>

              <div className="fut-card-series">
                <span title={t('app.brand')}>{t('app.brand').toUpperCase()}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="fut-card-actions">
          {!isOwnProfile && (
            <button
              type="button"
              onClick={handleFollow}
className={`flex items-center justify-center gap-2 font-['Orbitron'] text-xs uppercase tracking-[0.12em] rounded-sm ${
                  isFollowing
                    ? cardClass
                    : 'btn-gradient border-0'
                }`}
            >
              {isFollowing ? t('common.following') : t('common.follow')}
            </button>
          )}

          {isOwnProfile && (
            <>
              <button
                type="button"
                onClick={handleEnableNotifications}
                disabled={notificationsEnabled}
                className={`flex items-center justify-center gap-2 rounded-sm border-2 px-4 py-2 text-xs font-['Orbitron'] uppercase tracking-[0.12em] transition ${
                  notificationsEnabled
                    ? 'border-green-500/40 text-green-400 cursor-default'
                    : 'border-sport-primary/40 text-sport-primary hover:bg-sport-primary/10'
                }`}
              >
                {notificationsEnabled ? t('notifications.enabled', { defaultValue: '✓ Notifications On' }) : t('notifications.enable', { defaultValue: 'Enable Notifications' })}
              </button>
              <button
                type="button"
                onClick={handleOpenEdit}
                className="btn-outline flex items-center justify-center gap-2"
              >
                <PencilIcon className="h-4 w-4" />
                <span>{t('common.editLoadout')}</span>
              </button>
              <button
                type="button"
                onClick={handleLogoutFromProfile}
                className="flex items-center justify-center gap-2 rounded-sm border-2 border-red-500/40 px-4 py-2 text-xs font-['Orbitron'] uppercase tracking-[0.12em] text-red-400 transition hover:bg-red-500/10"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                <span>{t('common.logout')}</span>
              </button>
            </>
          )}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08 }}
        className="profile-panel p-6 sm:p-7 relative z-10"
      >
        <div className="profile-section-head relative z-10">
          <span className="head-accent" aria-hidden />
          <h2>{t('profile.intel')}</h2>
        </div>
        <div className="space-y-5 relative z-10">
          {isOwnProfile && (
            <div>
              <h3 className="text-sport-primary/70 text-[10px] font-['Orbitron'] uppercase tracking-[0.2em] mb-2">{t('profile.commsId')}</h3>
              <p className={`${lightTextClass} font-medium break-all`}>{profileUser?.email}</p>
              {profileUser?.phone && (
                <p className={`${mutedTextClass} text-sm mt-2`}>{profileUser.phone}</p>
              )}
            </div>
          )}

          <div>
            <h3 className="text-sport-primary/70 text-[10px] font-['Orbitron'] uppercase tracking-[0.2em] mb-2">{t('profile.sports')}</h3>
            <div className="flex flex-wrap gap-2">
              {sportsList.length > 0 ? (
                sportsList.map((sport) => (
                  <span
                    key={sport}
                    className={`${badgeClass} px-3 py-1.5 rounded-sm uppercase tracking-wide font-['Orbitron']`}
                  >
                    {sport}
                  </span>
                ))
              ) : (
                <span className={`${mutedTextLightestClass} text-sm`}>{t('profile.noSportsSelected')}</span>
              )}
            </div>
          </div>

          {isOwnProfile && (
            <div>
              <h3 className="text-sport-primary/70 text-[10px] font-['Orbitron'] uppercase tracking-[0.2em] mb-2">{t('profile.bio')}</h3>
              <p className={`${mutedTextClass} text-sm leading-relaxed border-l-2 border-sport-secondary/40 pl-3`}>
                {profileUser?.bio || t('profile.noBio')}
              </p>
            </div>
          )}

          <div className="mt-2 flex flex-wrap gap-3 pt-5 border-t border-sport-primary/10">
            <div className="profile-social-stat text-center flex-1 min-w-[5.5rem]">
              <div className={`text-lg font-bold font-['Orbitron'] ${lightTextClass} tabular-nums`}>
                {profileUser.followers?.length || 0}
              </div>
              <div className={`text-[10px] ${statsLabelClass} uppercase tracking-wider mt-0.5`}>{t('profile.followers')}</div>
            </div>
            <div className="profile-social-stat text-center flex-1 min-w-[5.5rem]">
              <div className={`text-lg font-bold font-['Orbitron'] ${lightTextClass} tabular-nums`}>
                {profileUser.following?.length || 0}
              </div>
              <div className={`text-[10px] ${statsLabelClass} uppercase tracking-wider mt-0.5`}>{t('profile.following')}</div>
            </div>
            <div className="profile-social-stat text-center flex-1 min-w-[5.5rem]">
              <div className={`text-lg font-bold font-['Orbitron'] ${lightTextClass} tabular-nums`}>{profileUser.goals || 0}</div>
              <div className={`text-[10px] ${statsLabelClass} uppercase tracking-wider mt-0.5`}>{t('profile.goals')}</div>
            </div>
          </div>
        </div>
      </motion.div>

      {isOwnProfile && (
        <Dialog open={isEditing} onClose={handleCloseEdit} className="relative z-[100]">
          <div className={`fixed inset-0 ${modalBackdropClass} backdrop-blur-md`} aria-hidden="true" />

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
              <Dialog.Panel className={`profile-edit-modal w-full max-w-lg ${modalPanelClass} shadow-2xl shadow-sport-primary/10 rounded-lg p-5 sm:p-7 relative`}>
                <div className="flex items-start justify-between gap-3 mb-6 pb-4 border-b border-sport-primary/15">
                  <Dialog.Title className={`font-['Orbitron'] text-lg font-bold tracking-wide ${lightTextClass} uppercase flex items-center gap-2 pr-2`}>
                    <span className="w-1 h-6 bg-gradient-to-b from-sport-primary to-sport-secondary rounded-sm shrink-0" />
                    {t('profile.editLoadoutTitle')}
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={handleCloseEdit}
                    className={`p-2 rounded-lg ${darkMode ? 'text-sport-silver hover:text-sport-light' : 'text-gray-500 hover:text-gray-800'} hover:bg-sport-primary/10 transition shrink-0`}
                    aria-label={t('common.close')}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <input
                  type="file"
                  ref={profileImageRef}
                  className="hidden"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileSelect}
                />

                <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-start mb-6 pb-6 border-b border-sport-primary/15">
                  <div className={`w-28 h-28 sm:w-32 sm:h-32 rounded-xl overflow-hidden border-2 border-sport-primary/45 ${avatarBgClass} shrink-0 shadow-lg shadow-sport-primary/10`}>
                    {profileUser?.profileImage ? (
                      <img
                        src={profileUser.profileImage}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none'
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl font-bold font-['Orbitron'] text-sport-primary/80">
                        {profileUser?.firstName?.[0]}
                        {profileUser?.lastName?.[0]}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full sm:w-auto sm:pt-1">
                    <button
                      type="button"
                      onClick={handleProfileImageClick}
                      disabled={uploadingImage}
                      className="btn-outline flex items-center justify-center gap-2 text-sm py-2.5 font-['Orbitron'] uppercase tracking-wide"
                    >
                      <CameraIcon className="h-4 w-4" />
                      {uploadingImage ? t('profile.uploading') : t('profile.changePhoto')}
                    </button>
                    {profileUser?.profileImage && (
                      <button
                        type="button"
                        onClick={handleDeleteImage}
                        disabled={uploadingImage}
                        className="flex items-center justify-center gap-2 text-sm py-2.5 rounded-xl border-2 border-red-500/40 text-red-400 font-['Orbitron'] uppercase tracking-wide hover:bg-red-500/10 transition"
                      >
                        <TrashIcon className="h-4 w-4" />
                        {t('profile.removePhoto')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={editForm.firstName || ''}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    className="input-modern"
                    placeholder={t('auth.firstName')}
                  />
                  <input
                    type="text"
                    value={editForm.lastName || ''}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    className="input-modern"
                    placeholder={t('auth.lastName')}
                  />
                  <input
                    type="tel"
                    value={editForm.phone || ''}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="input-modern"
                    placeholder={t('auth.phone')}
                  />
                  <input
                    type="text"
                    value={editForm.city || ''}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    className="input-modern"
                    placeholder={t('auth.city')}
                  />
                  {isOwnProfile && (
                    <button
                      type="button"
                      onClick={handleDetectLocation}
                      disabled={detectingLocation}
                      className="input-modern flex items-center justify-center gap-2 bg-sport-primary/10 text-sport-primary hover:bg-sport-primary/20 transition disabled:opacity-50"
                    >
                      {detectingLocation ? (
                        <div className="w-4 h-4 border-2 border-sport-primary border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <MapPinIcon className="h-4 w-4" />
                      )}
                      <span className="text-sm">{t('location.detectLocation')}</span>
                    </button>
                  )}
                  {editForm.latitude && editForm.longitude && (
                    <p className="text-xs text-green-600 col-span-2">
                      {t('location.coordinatesSet')}: {editForm.latitude.toFixed(4)}, {editForm.longitude.toFixed(4)}
                    </p>
                  )}
                  <input
                    type="number"
                    min="0"
                    max="99"
                    value={editForm.playerNumber ?? ''}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        playerNumber: e.target.value.replace(/\D/g, '').slice(0, 2)
                      })
                    }
                    className="input-modern no-spinner"
                    placeholder={t('profile.playerNumber')}
                  />
                  <select
                    value={editSportsValue}
                    onChange={(e) => setEditForm({ ...editForm, sports: e.target.value })}
                    className="input-modern"
                  >
                    <option value="calcio">{t('sports.calcio')}</option>
                    <option value="padel">{t('sports.padel')}</option>
                    <option value="both">{t('sports.both')}</option>
                  </select>
                  <select
                    value={editForm.role || ''}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                    className="input-modern"
                  >
                    <option value="">{t('profile.selectRole')}</option>
                    {editForm.sports === 'calcio' && (
                      <>
                        <option value="goalkeeper">{t('role.goalkeeper')}</option>
                        <option value="defender">{t('role.defender')}</option>
                        <option value="midfielder">{t('role.midfielder')}</option>
                        <option value="attacker">{t('role.attacker')}</option>
                      </>
                    )}
                    {editForm.sports === 'padel' && (
                      <>
                        <option value="left">{t('role.left')}</option>
                        <option value="right">{t('role.right')}</option>
                      </>
                    )}
                    {editForm.sports === 'both' && (
                      <>
                        <optgroup label={t('sports.calcio')}>
                          <option value="goalkeeper">{t('role.goalkeeper')}</option>
                          <option value="defender">{t('role.defender')}</option>
                          <option value="midfielder">{t('role.midfielder')}</option>
                          <option value="attacker">{t('role.attacker')}</option>
                        </optgroup>
                        <optgroup label={t('sports.padel')}>
                          <option value="left">{t('role.left')}</option>
                          <option value="right">{t('role.right')}</option>
                        </optgroup>
                      </>
                    )}
                  </select>
                  <textarea
                    value={editForm.bio || ''}
                    onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                    className="input-modern md:col-span-2 min-h-[100px] resize-y"
                    placeholder={t('profile.bioOptional')}
                    rows={4}
                  />
                </div>

                <div className="flex flex-wrap gap-3 mt-6 pt-2">
                  <button
                    type="button"
                    onClick={handleUpdateProfile}
                    className="btn-gradient flex items-center gap-2 font-['Orbitron'] text-sm uppercase tracking-wide rounded-sm"
                  >
                    <CheckCircleIcon className="h-4 w-4" />
                    <span>{t('common.apply')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseEdit}
                    className="btn-outline flex items-center gap-2 font-['Orbitron'] text-sm uppercase tracking-wide rounded-sm"
                  >
                    <XMarkIcon className="h-4 w-4" />
                    <span>{t('common.cancel')}</span>
                  </button>
                </div>
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.16 }}
        className="profile-panel p-6 sm:p-7 relative z-10"
      >
        <div className="profile-section-head relative z-10">
          <span className="head-accent" aria-hidden />
          <h2 className="!text-base flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-sport-primary shrink-0" />
            {t('profile.matchHistory')}
          </h2>
        </div>
        {sortedUserMatches.length > 0 ? (
          <div className="space-y-5 relative z-10">
            <div className={`flex flex-wrap gap-2 rounded-xl border border-sport-primary/15 ${filterTabBgClass} p-1`}>
              <button
                type="button"
                onClick={() => setHistoryTab('organized')}
                className={`flex-1 min-w-[10rem] rounded-lg px-4 py-2 text-xs font-['Orbitron'] uppercase tracking-[0.14em] transition ${
                  historyTab === 'organized'
                    ? 'bg-sport-secondary text-white shadow-lg shadow-sport-secondary/20'
                    : tabInactiveClass
                }`}
              >
                Organized by ({organizedMatches.length})
              </button>
              <button
                type="button"
                onClick={() => setHistoryTab('played')}
                className={`flex-1 min-w-[10rem] rounded-lg px-4 py-2 text-xs font-['Orbitron'] uppercase tracking-[0.14em] transition ${
                  historyTab === 'played'
                    ? 'bg-sport-primary text-white shadow-lg shadow-sport-primary/20'
                    : tabInactiveClass
                }`}
              >
                Played ({playedMatches.length})
              </button>
            </div>

            <div className="space-y-3">
              {(historyTab === 'organized' ? organizedMatches : playedMatches).slice(0, 5).map((match) => (
                <motion.div
                  key={match.id}
                  whileHover={{ x: 6 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                  className="profile-match-card group cursor-pointer pl-5"
                  onClick={() => navigate(`/match/${match.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(`/match/${match.id}`)
                    }
                  }}
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className={`${lightTextClass} font-['Orbitron'] font-semibold text-sm sm:text-base group-hover:text-sport-primary transition-colors`}>
                        {match.title || `${match.sport === 'calcio' ? 'Football' : 'Padel'} Match`}
                      </h3>
                      <div className={`flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs sm:text-sm ${mutedTextLightClass}`}>
                        <span className="flex items-center gap-1.5">
                          <CalendarIcon className="h-3.5 w-3.5 text-sport-primary shrink-0" />
                          {format(new Date(match.date), 'MMM d, yyyy')}
                        </span>
                        <span className="flex items-center gap-1.5 min-w-0">
                          <MapPinIcon className="h-3.5 w-3.5 text-sport-secondary shrink-0" />
                          <span className="truncate">{match.location}</span>
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {historyTab === 'organized' ? (
                          <span className="inline-flex items-center rounded-full border border-sport-secondary/30 bg-sport-secondary/10 px-2.5 py-1 text-[10px] font-['Orbitron'] uppercase tracking-wider text-sport-secondary">
                            {t('common.organizer')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full border border-sport-primary/30 bg-sport-primary/10 px-2.5 py-1 text-[10px] font-['Orbitron'] uppercase tracking-wider text-sport-primary">
                            {t('profile.contribution', { defaultValue: 'Contribution' })}: {getMatchContributionLabel(match)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="text-[10px] font-['Orbitron'] uppercase tracking-wider text-sport-primary/80 block">
                        {t('profile.squad', { defaultValue: 'Squad' })}
                      </span>
                      <span className="text-sm font-bold font-['Orbitron'] {lightTextClass} tabular-nums">
                        {match.currentPlayers}/{match.maxPlayers}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}

              {(historyTab === 'organized' ? organizedMatches : playedMatches).length === 0 && (
                <p className={`${mutedTextLighterClass} text-sm relative z-10 pl-1`}>
                  {historyTab === 'organized' ? t('profile.noOrganizedMatches', { defaultValue: 'No organized matches yet.' }) : t('profile.noPlayedMatches', { defaultValue: 'No played-in matches yet.' })}
                </p>
              )}
            </div>
          </div>
        ) : (
          <p className={`${mutedTextLighterClass} text-sm relative z-10 pl-1`}>{t('profile.noMatchesLogged')}</p>
        )}
      </motion.div>
    </div>
  )
}

export default Profile
